/**
 * MedexOvaColdLoad — the unified brand cold-load applied to every entry point.
 * See /app/memory/DESIGN_LANGUAGE_v2.md §5.
 */
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { DUR, EASE, ROLE_ACCENT, ROLE_ACCENT_GLOW, prefersReducedMotion } from "../lib/motion";

const CAPTIONS = {
  rider: "verifying your ride link",
  driver: "opening your beacon",
  tp: "preparing your ops center",
  admin: "establishing secure channel",
  broker: "unlocking the ingestion queue",
};

export default function MedexOvaColdLoad({ role = "admin", minMs = 1400, onDone }) {
  const [caption, setCaption] = useState("");
  const [visible, setVisible] = useState(true);
  const accent = ROLE_ACCENT[role] || ROLE_ACCENT.admin;
  const glow = ROLE_ACCENT_GLOW[role] || ROLE_ACCENT_GLOW.admin;
  const target = CAPTIONS[role] || CAPTIONS.admin;
  const reduced = prefersReducedMotion();

  useEffect(() => {
    if (reduced) {
      setCaption(target);
      const t = setTimeout(() => { setVisible(false); onDone && onDone(); }, 200);
      return () => clearTimeout(t);
    }
    // Typewriter
    let i = 0;
    const iv = setInterval(() => {
      i += 1;
      setCaption(target.slice(0, i));
      if (i >= target.length) clearInterval(iv);
    }, 24);
    // Dismiss
    const t = setTimeout(() => {
      setVisible(false);
      onDone && onDone();
    }, minMs);
    return () => { clearInterval(iv); clearTimeout(t); };
  }, [role, minMs, onDone, target, reduced]);

  if (!visible) return null;

  return (
    <motion.div
      data-testid="cold-load-root"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      animate={{ opacity: visible ? 1 : 0 }}
      transition={{ duration: DUR.smooth, ease: EASE.settle }}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "linear-gradient(135deg,#0B1220 0%,#142433 50%,#0B1220 100%)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        color: "#fff",
      }}
    >
      {/* backdrop glow */}
      <div aria-hidden style={{
        position: "absolute", inset: 0,
        background: `radial-gradient(700px 400px at 50% 45%, ${glow} 0%, transparent 60%)`,
        pointerEvents: "none",
      }}/>
      {/* logo */}
      <motion.div
        data-testid="cold-load-logo"
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: DUR.reveal, ease: EASE.emerge, delay: 0.1 }}
        style={{
          width: 84, height: 84, borderRadius: "50%",
          background: `radial-gradient(circle, ${accent} 0%, transparent 70%)`,
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 24, position: "relative",
        }}
      >
        <div style={{
          width: 46, height: 46, borderRadius: 8, border: `2.5px solid ${accent}`,
          transform: "rotate(45deg)", position: "relative",
        }}/>
      </motion.div>
      {/* wordmark */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: DUR.reveal, ease: EASE.emerge, delay: 0.28 }}
        style={{
          fontFamily: "ui-monospace,Menlo,monospace",
          fontSize: 18, letterSpacing: "0.42em", fontWeight: 700,
          color: "#E2E8F0", marginBottom: 26,
        }}
      >
        {role === "tp" ? (
          <span style={{ letterSpacing: "0.05em" }}>
            <span style={{ color: "#16A34A" }}>Medex</span><span style={{ color: "#C4B5FD" }}>OVA</span>
          </span>
        ) : (
          <span style={{ letterSpacing: "0.05em" }}>
            <span style={{ color: "#16A34A" }}>MEDEX</span><span style={{ color: "#C4B5FD" }}>OVA</span>
          </span>
        )}
      </motion.div>
      {/* progress bar */}
      <div style={{
        width: "min(320px,60vw)", height: 3, borderRadius: 999,
        background: "rgba(255,255,255,0.08)", overflow: "hidden", marginBottom: 16,
      }}>
        <motion.div
          data-testid="cold-load-progress"
          initial={{ width: 0 }}
          animate={{ width: reduced ? "100%" : "100%" }}
          transition={{ duration: reduced ? DUR.snap : (minMs / 1000) * 0.85, ease: EASE.linear, delay: 0.52 }}
          style={{ height: "100%", background: accent, boxShadow: `0 0 12px ${accent}` }}
        />
      </div>
      {/* caption */}
      <div
        data-testid="cold-load-caption"
        style={{
          fontFamily: "ui-monospace,Menlo,monospace",
          fontSize: 11, letterSpacing: "0.18em", color: "#94A3B8",
          textTransform: "uppercase", minHeight: 14,
        }}
      >
        → {caption}
      </div>
    </motion.div>
  );
}
